/**
 * Theme-aware icon exports using Magnetic Design System
 * 
 * Icons are imported from @magnetic/icons when available with proper defaults:
 * - weight="bold"
 * - color="currentColor" (inherits container text color)
 * 
 * Icons not available in Magnetic are imported from @phosphor-icons/react directly.
 * These defaults can be overridden per usage.
 */
import {
  SignOut as SignOutBase,
  UserCircle as UserCircleBase,
  Folder as FolderBase,
  FolderOpen as FolderOpenBase,
  ArrowSquareOut as ArrowSquareOutBase,
  Warning as WarningBase,
  Trash as TrashBase,
  Export as ExportBase,
  Package as PackageBase,
  Key as KeyBase,
  Stack as StackBase,
  Broadcast as BroadcastBase,
  Chat as ChatBase,
  XCircle as XCircleBase,
  Circle as CircleBase,
  Clock as ClockBase,
  CheckCircle as CheckCircleBase,
  GitBranch as GitBranchBase,
  Users as UsersBase,
  SpinnerGap as SpinnerGapBase,
  UserPlus as UserPlusBase,
  FileCode as FileCodeBase,
  ClockCounterClockwise as ClockCounterClockwiseBase,
  Terminal as TerminalBase,
  Bell as BellBase,
  Flask as FlaskBase,
  WarningCircle as WarningCircleBase,
  FloppyDisk as FloppyDiskBase,
  TrendUp as TrendUpBase,
  ChartBar as ChartBarBase,
  Play as PlayBase,
  Pause as PauseBase,
  X as XBase,
  ShieldCheck as ShieldCheckBase,
  ArrowsClockwise as ArrowsClockwiseBase,
  Funnel as FunnelBase,
  HardDrives as HardDrivesBase,
  ArrowRight as ArrowRightBase,
  Download as DownloadBase,
  MagnifyingGlass as MagnifyingGlassBase,
  User as UserBase,
  Database as DatabaseBase,
  ArrowLeft as ArrowLeftBase,
  Question as QuestionBase,
  Gear as GearBase,
  // Magnetic Status Icons - exported directly (already themed)
  Excellent,
  Positive,
  LowWarning,
  Warning as WarningStatus,
  SevereWarning,
  Negative,
  Dormant,
  Information,
  Disabled as DisabledStatus,
  InProgress,
  Deny,
  Approve,
} from '@magnetic/icons'

// Icons not available in Magnetic - import from Phosphor directly
import {
  Activity as ActivityBase,
  Plus as PlusBase,
  Cube as CubeBase,
  Calendar as CalendarBase,
  Power as PowerBase,
  CircleNotch as CircleNotchBase,
  Monitor as MonitorBase,
} from '@phosphor-icons/react'

import type { IconProps } from '@magnetic/icons'
import type { ComponentType } from 'react'

// Helper to create icons with Magnetic defaults
const createIcon = (IconComponent: ComponentType<IconProps>) => {
  const ThemedIcon = (props: Partial<IconProps>) => (
    <IconComponent
      weight="bold"
      color="currentColor"
      {...props}
    />
  )
  ThemedIcon.displayName = `Themed${IconComponent.displayName || 'Icon'}`
  return ThemedIcon
}

// Export themed versions
export const SignOut = createIcon(SignOutBase)
export const UserCircle = createIcon(UserCircleBase)
export const Folder = createIcon(FolderBase)
export const FolderOpen = createIcon(FolderOpenBase)
export const ArrowSquareOut = createIcon(ArrowSquareOutBase)
export const Warning = createIcon(WarningBase)
export const Trash = createIcon(TrashBase)
export const Export = createIcon(ExportBase)
export const Package = createIcon(PackageBase)
export const Key = createIcon(KeyBase)
export const Stack = createIcon(StackBase)
export const Broadcast = createIcon(BroadcastBase)
export const Chat = createIcon(ChatBase)
export const XCircle = createIcon(XCircleBase)
export const Circle = createIcon(CircleBase)
export const Clock = createIcon(ClockBase)
export const CheckCircle = createIcon(CheckCircleBase)
export const GitBranch = createIcon(GitBranchBase)
export const Users = createIcon(UsersBase)
export const SpinnerGap = createIcon(SpinnerGapBase)
export const UserPlus = createIcon(UserPlusBase)
export const FileCode = createIcon(FileCodeBase)
export const ClockCounterClockwise = createIcon(ClockCounterClockwiseBase)
export const Terminal = createIcon(TerminalBase)
export const Bell = createIcon(BellBase)
export const Flask = createIcon(FlaskBase)
export const WarningCircle = createIcon(WarningCircleBase)
export const FloppyDisk = createIcon(FloppyDiskBase)
export const TrendUp = createIcon(TrendUpBase)
export const ChartBar = createIcon(ChartBarBase)
export const Play = createIcon(PlayBase)
export const Pause = createIcon(PauseBase)
export const X = createIcon(XBase)
export const ShieldCheck = createIcon(ShieldCheckBase)
export const ArrowsClockwise = createIcon(ArrowsClockwiseBase)
export const Funnel = createIcon(FunnelBase)
export const HardDrives = createIcon(HardDrivesBase)
export const ArrowRight = createIcon(ArrowRightBase)
export const Download = createIcon(DownloadBase)
export const MagnifyingGlass = createIcon(MagnifyingGlassBase)
export const User = createIcon(UserBase)
export const Database = createIcon(DatabaseBase)
export const ArrowLeft = createIcon(ArrowLeftBase)
export const Question = createIcon(QuestionBase)
export const Gear = createIcon(GearBase)

// Icons from Phosphor (not in Magnetic)
export const Activity = createIcon(ActivityBase)
export const Plus = createIcon(PlusBase)
export const Cube = createIcon(CubeBase)
export const Calendar = createIcon(CalendarBase)
export const Power = createIcon(PowerBase)
export const CircleNotch = createIcon(CircleNotchBase)
export const Monitor = createIcon(MonitorBase)

// Magnetic Status Icons - exported directly (already themed with built-in backgrounds)
export { Excellent, Positive, LowWarning, SevereWarning, Negative, Dormant, Information, InProgress, Deny, Approve }
// Re-export with clear names to avoid conflicts
export { WarningStatus, DisabledStatus }
